import React from 'react';

import {
  fetchPlacesStatus,
  fetchSuggestions as requestSuggestions,
  fetchPlaceDetails,
  createSessionToken
} from './placesClient';
import {
  mapPlaceToAddress,
  buildAddressPatch,
  staleAutofillPatch,
  toSuggestionOption
} from './placesAddressMapping';
import { findCountry, resolveCountryCode } from './geoReference';

const DEFAULT_MIN_LENGTH = 3;
const DEFAULT_DEBOUNCE_MS = 300;
const DEFAULT_FIELD_NAMES = {
  line1: 'addressLine1',
  line2: 'addressLine2',
  city: 'city',
  town: 'town',
  county: 'county',
  state: 'state',
  postCode: 'postCode',
  country: 'country'
};

const MAX_REGION_CODES = 15;

const EMPTY_COUNTRIES = [];

function countryKey(codes) {
  const list = Array.isArray(codes) ? codes : [codes].filter(Boolean);
  return [
    ...new Set(
      list.filter(Boolean).map((code) => String(code).trim().toUpperCase())
    )
  ]
    .sort()
    .slice(0, MAX_REGION_CODES)
    .join(',');
}

function listCountries(names) {
  if (names.length === 0) return 'the countries this site works in';
  if (names.length === 1) return names[0];
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

export function useAddressAutocomplete({
  countryCodes = EMPTY_COUNTRIES,
  reference,
  values,
  onChange,
  fieldNames = DEFAULT_FIELD_NAMES,
  enabled = true,
  fhqToken,
  minLength = DEFAULT_MIN_LENGTH,
  debounceMs = DEFAULT_DEBOUNCE_MS
}) {
  const [siteCountries, setSiteCountries] = React.useState(EMPTY_COUNTRIES);

  const explicitKey = countryKey(countryCodes);
  const siteKey = countryKey(siteCountries);
  const scopeKey = explicitKey || siteKey;

  const scope = React.useMemo(
    () => (scopeKey ? scopeKey.split(',') : []),
    [scopeKey]
  );

  const [inputValue, setInputValue] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [configured, setConfigured] = React.useState(false);
  const [status, setStatus] = React.useState('ok');

  const sessionRef = React.useRef(null);
  const timerRef = React.useRef(null);
  const seqRef = React.useRef(0);
  const mountedRef = React.useRef(true);
  const autofillRef = React.useRef(null);

  const valuesRef = React.useRef(values);
  valuesRef.current = values;
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  const fieldNamesRef = React.useRef(fieldNames);
  fieldNamesRef.current = fieldNames;
  const fhqTokenRef = React.useRef(fhqToken);
  fhqTokenRef.current = fhqToken;

  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  React.useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const apply = ({ enabled: isEnabled, countries }) => {
      if (cancelled) return;
      setConfigured(isEnabled);
      setSiteCountries((prev) =>
        countryKey(prev) === countryKey(countries) ? prev : countries
      );
      setStatus((prev) => {
        if (!isEnabled) return 'unavailable';
        return prev === 'unavailable' ? 'ok' : prev;
      });
    };

    const load = () => fetchPlacesStatus(fhqTokenRef.current).then(apply);
    load();
    const recheck = () => {
      if (document.visibilityState === 'hidden') return;
      load();
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [enabled]);

  const active = enabled && configured;

  const resetSession = React.useCallback(() => {
    sessionRef.current = null;
  }, []);

  React.useEffect(() => {
    const previous = autofillRef.current;
    if (previous && previous.scopeKey === scopeKey) return;

    autofillRef.current = null;
    resetSession();
    seqRef.current += 1;
    clearTimeout(timerRef.current);
    setInputValue('');
    setSuggestions([]);
    setLoading(false);
    setStatus('ok');

    if (!previous) return;
    const patch = staleAutofillPatch(previous.values, valuesRef.current);
    if (Object.keys(patch).length > 0) onChangeRef.current?.(patch);
  }, [scopeKey, resetSession]);

  const runSearch = React.useCallback(
    async (input, requestId) => {
      if (!sessionRef.current) sessionRef.current = createSessionToken();

      try {
        const results = await requestSuggestions({
          input,
          regionCodes: scope.length ? scope : undefined,
          sessionToken: sessionRef.current,
          fhqToken: fhqTokenRef.current
        });
        if (!mountedRef.current || requestId !== seqRef.current) return;
        setSuggestions(results.map(toSuggestionOption).filter(Boolean));
        setStatus('ok');
      } catch {
        if (!mountedRef.current || requestId !== seqRef.current) return;
        setSuggestions([]);
        setStatus('request-failed');
      } finally {
        if (mountedRef.current && requestId === seqRef.current) {
          setLoading(false);
        }
      }
    },
    [scope]
  );

  const handleInputChange = React.useCallback(
    (next) => {
      const value = next ?? '';
      setInputValue(value);
      clearTimeout(timerRef.current);
      // A new query supersedes the rejected one — don't leave the mismatch
      // error sitting under the field while the user retypes.
      setStatus((prev) => (prev === 'country-mismatch' ? 'ok' : prev));

      const trimmed = value.trim();
      if (trimmed.length < minLength) {
        seqRef.current += 1;
        setSuggestions([]);
        setLoading(false);
        if (trimmed.length === 0) resetSession();
        return;
      }
      if (!active) return;

      setLoading(true);
      const requestId = ++seqRef.current;
      timerRef.current = setTimeout(
        () => runSearch(trimmed, requestId),
        debounceMs
      );
    },
    [active, debounceMs, minLength, resetSession, runSearch]
  );

  const handleSelect = React.useCallback(
    async (option) => {
      if (!option?.placeId) return;

      clearTimeout(timerRef.current);
      seqRef.current += 1;
      setSuggestions([]);
      setLoading(true);

      const sessionToken = sessionRef.current;
      try {
        const place = await fetchPlaceDetails({
          placeId: option.placeId,
          sessionToken,
          fhqToken: fhqTokenRef.current
        });
        resetSession();
        if (!mountedRef.current) return;

        const mapped = mapPlaceToAddress(place, {
          reference,
          fallbackLine1: option.primary
        });

        if (
          scope.length &&
          mapped.googleCountryCode &&
          !scope.includes(mapped.googleCountryCode)
        ) {
          setStatus('country-mismatch');
          return;
        }

        const names = fieldNamesRef.current;
        const currentCountry = names.country
          ? resolveCountryCode(reference, valuesRef.current?.[names.country])
          : '';
        const includeCountry =
          Boolean(mapped.country) && mapped.countryCode !== currentCountry;
        const patch = buildAddressPatch(mapped, names, { includeCountry });
        const tracked = { ...patch };
        if (names.country) delete tracked[names.country];
        const wroteCountry = Boolean(names.country && patch[names.country]);
        autofillRef.current = {
          scopeKey: wroteCountry ? mapped.countryCode : scopeKey,
          values: tracked
        };

        setInputValue(mapped.formattedAddress || option.label || '');
        setStatus('ok');
        if (Object.keys(patch).length > 0) onChangeRef.current?.(patch);
      } catch {
        if (!mountedRef.current) return;
        resetSession();
        setStatus('request-failed');
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [scope, scopeKey, reference, resetSession]
  );

  const countryNames = React.useMemo(
    () => scope.map((code) => findCountry(reference, code)?.name || code),
    [scope, reference]
  );

  const statusMessage =
    status === 'country-mismatch'
      ? `That address is not in ${listCountries(
          countryNames
        )} — change the country, or pick another result`
      : status === 'request-failed'
      ? 'Address lookup is temporarily unavailable — enter the address manually'
      : '';

  return {
    show: active,
    loading,
    status,
    statusMessage,
    countryCodes: scope,
    siteCountries,
    inputValue,
    suggestions,
    handleInputChange,
    handleSelect
  };
}
